import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../firebase/AuthProvider';
import { resourceChat, ResourceChatChoice } from '../services/resources/ResourceChatService';

/** Resource searches use the same per-viewer directory policy as listing previews. */
export function useResourceReferenceSearch() {
  const { currentUser, currentUserInfo, currentRoles } = useAuth();
  const roles = (currentRoles ?? []).map(role => role.name).sort().join('|');
  const scope = `${currentUser?.uid ?? ''}:${currentUserInfo?.id ?? ''}:${currentUserInfo?.organization?.id ?? ''}:${roles}`;
  const scopeRef = useRef(scope); scopeRef.current = scope;
  const pending = useRef<{ timer?: number; controller: AbortController; settle: (rows: ResourceChatChoice[]) => void }>();
  const cancel = useCallback(() => {
    window.clearTimeout(pending.current?.timer);
    pending.current?.controller.abort();
    pending.current?.settle([]);
    pending.current = undefined;
  }, []);
  useEffect(() => { scopeRef.current = scope; cancel(); return () => { scopeRef.current = ''; cancel(); }; }, [scope, cancel]);
  const search = useCallback((query: string): Promise<ResourceChatChoice[]> => {
    cancel();
    if (!currentUser || query.length > 200) return Promise.resolve([]);
    return new Promise(resolve => {
      const operation = { controller: new AbortController(), settle: resolve, timer: undefined as number | undefined };
      pending.current = operation;
      operation.timer = window.setTimeout(async () => {
        try {
          const token = await currentUser.getIdToken();
          if (operation.controller.signal.aborted || scopeRef.current !== scope) return;
          const rows = await resourceChat.search(token, query.trim(), operation.controller.signal);
          resolve(!operation.controller.signal.aborted && scopeRef.current === scope ? rows : []);
        } catch { resolve([]); }
        finally { if (pending.current === operation) pending.current = undefined; }
      }, 200);
    });
  }, [cancel, currentUser, scope]);
  const preview = useCallback(async (id: string) => {
    if (!currentUser) throw new Error('Resource unavailable.');
    const token = await currentUser.getIdToken();
    if (scopeRef.current !== scope) throw new Error('Resource unavailable.');
    await resourceChat.preview(token, id);
    if (scopeRef.current !== scope) throw new Error('Resource unavailable.');
  }, [currentUser, scope]);
  return { search, preview, cancel };
}
